import { NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  computed,
  effect,
  input,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { ClosingField, ClosingFieldCatalogItem } from '../../models/catalogs/catalogs-model';
import { InputSelect } from '../../models/pages-model/form-general.model';
import { InputFileLightComponent } from '../../inputs/input-file-light/input-file-light.component';
import { InputGeneral } from '../../inputs/input-general/input-general';
import { InputGeneralSelectComponent } from '../../inputs/input-general-select/input-general-select.component';
import { InputGeneralTextComponent } from '../../inputs/input-general-text/input-general-text.component';
import { CatalogsService } from '../../services/catalogs.service';
import { PostalCodeService } from '../../services/postal-code.service';

export interface DynamicCodeDependency {
  codeSource: number;
  codeTarget: number;
  codeMunicipality?: number;
}

interface ClosingFieldGroup {
  category: string;
  fields: ClosingField[];
}

@Component({
  selector: 'app-form-dinamic',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgClass,
    InputGeneral,
    InputGeneralSelectComponent,
    InputGeneralTextComponent,
    InputFileLightComponent,
  ],
  templateUrl: './form-dinamic.component.html',
  styleUrl: './form-dinamic.component.css',
})
export class FormDinamicComponent implements OnDestroy {
  public readonly closingFields = input<ClosingField[]>([]);
  @Input() public colummMax = 2;
  @Input() public codeDependencies: DynamicCodeDependency[] = [];
  @Output() public formGeneralChange = new EventEmitter<FormGroup>();

  public readonly catalogInputSelect: InputSelect = { value: 'Id', description: 'Value' };

  private subscription: Subscription | null = null;
  private dependencySubscriptions: Subscription[] = [];
  private catalogSubscriptions: Subscription[] = [];
  private managedControlNames = new Set<string>();
  private _formGeneral: FormGroup = new FormGroup({});

  public readonly groupedClosingFields = computed<ClosingFieldGroup[]>(() => {
    const groups = new Map<string, ClosingField[]>();
    const fields = [...this.closingFields()].sort((a, b) => a.Order - b.Order);

    for (const field of fields) {
      const category = field.ClosingFieldCategory?.trim() || 'Campos adicionales';
      const currentGroup = groups.get(category) ?? [];
      currentGroup.push(field);
      groups.set(category, currentGroup);
    }

    return Array.from(groups.entries()).map(([category, groupFields]) => ({
      category,
      fields: groupFields,
    }));
  });

  constructor(
    private postalCodeService: PostalCodeService,
    private catalogsService: CatalogsService,
  ) {
    effect(() => {
      this.syncFormControls(this.closingFields());
    });
  }

  @Input()
  public set formGeneral(value: FormGroup) {
    this._formGeneral = value ?? new FormGroup({});
    this.subscribeToFormChanges();
    this.syncFormControls(this.closingFields());
  }

  public get formGeneral(): FormGroup {
    return this._formGeneral;
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.clearDependencySubscriptions();
    this.clearCatalogSubscriptions();
  }

  public getControlName(field: ClosingField): string {
    return `closingField_${field.EditClosingFieldId || field.Id}`;
  }

  public getFieldControl(field: ClosingField): FormControl {
    return field.FormControl as FormControl;
  }

  public getSelectOptions(field: ClosingField): any[] {
    if (!field.ValidationRegex) {
      return [];
    }
    return field.ValidationRegex.replace(/[()]/g, '')
      .split('|')
      .map((item) => item.trim())
      .filter((item) => item !== '' && item !== '-');
  }

  public getCatalogOptions(field: ClosingField): ClosingFieldCatalogItem[] {
    return field.Catalog ?? [];
  }

  public getFileAccept(field: ClosingField): string {
    if (!field.ValidationRegex) {
      return '';
    }
    return field.ValidationRegex.replace(/[()]/g, '')
      .split('|')
      .map((ext) => ext.trim())
      .filter((ext) => !!ext)
      .map((ext) => (ext.startsWith('.') ? ext : `.${ext}`))
      .join(', ');
  }

  private subscribeToFormChanges(): void {
    this.subscription?.unsubscribe();
    this.subscription = this.formGeneral.valueChanges.subscribe(() => {
      this.formGeneralChange.emit(this.formGeneral);
    });
  }

  private syncFormControls(fields: ClosingField[]): void {
    const nextManagedControlNames = new Set<string>();

    for (const field of fields) {
      const controlName = this.getControlName(field);
      nextManagedControlNames.add(controlName);

      const control = field.FormControl ?? this.createControl(field);
      this.configureControl(control, field);
      field.FormControl = control;

      const currentControl = this.formGeneral.get(controlName);
      if (!currentControl) {
        this.formGeneral.addControl(controlName, control);
      } else if (currentControl !== control) {
        this.formGeneral.setControl(controlName, control);
      }

      if (field.ComponentType === 'CatalogDropDownList' && (!field.Catalog || field.Catalog.length === 0)) {
        this.loadCatalogOptions(field);
      }
    }

    for (const controlName of this.managedControlNames) {
      if (!nextManagedControlNames.has(controlName) && this.formGeneral.contains(controlName)) {
        this.formGeneral.removeControl(controlName);
      }
    }

    this.managedControlNames = nextManagedControlNames;
    this.initCodeDependencies();
  }

  public setupCodeDependency(
    sourceCode: number,
    targetCode: number,
    municipalityCode?: number,
  ): void {
    const fields = this.closingFields();
    const sourceField = fields.find((field) => field.Code === sourceCode);
    const targetField = fields.find((field) => field.Code === targetCode);
    const municipalityField = fields.find((field) => field.Code === (municipalityCode ?? 8));

    if (!sourceField || !targetField || !sourceField.FormControl) {
      return;
    }

    const sourceControl = sourceField.FormControl;

    const populateTarget = (value: any) => {
      if (!value || sourceControl.invalid) {
        targetField.ValidationRegex = '(-)';
        targetField.FormControl?.setValue('');
        if (municipalityField?.FormControl) {
          municipalityField.FormControl.setValue('');
        }
        return;
      }

      const sub = this.postalCodeService.getLocalities(value).subscribe((res) => {
        if (res?.Data && res.Data.length > 0) {
          const localities = res.Data.map((item) => item.Locality)
            .map((item) => item.trim())
            .filter((item) => !!item);
          targetField.ValidationRegex = `(${localities.join('|')})`;
          if (municipalityField?.FormControl) {
            const municipality = res.Data[0]?.Municipality?.trim() || '';
            municipalityField.FormControl.setValue(municipality);
          }
        } else {
          targetField.ValidationRegex = '(-)';
          targetField.FormControl?.setValue('');
          if (municipalityField?.FormControl) {
            municipalityField.FormControl.setValue('');
          }
        }
      });
      this.dependencySubscriptions.push(sub);
    };

    const valueChangesSub = sourceControl.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(300))
      .subscribe((value) => {
        populateTarget(value);
      });

    this.dependencySubscriptions.push(valueChangesSub);

    if (sourceControl.valid && sourceControl.value) {
      populateTarget(sourceControl.value);
    }
  }

  private initCodeDependencies(): void {
    this.clearDependencySubscriptions();

    if (!this.codeDependencies || this.codeDependencies.length === 0) {
      return;
    }

    for (const dependency of this.codeDependencies) {
      if (dependency?.codeSource && dependency?.codeTarget) {
        this.setupCodeDependency(
          dependency.codeSource,
          dependency.codeTarget,
          dependency.codeMunicipality,
        );
      }
    }
  }

  private clearDependencySubscriptions(): void {
    for (const sub of this.dependencySubscriptions) {
      sub.unsubscribe();
    }
    this.dependencySubscriptions = [];
  }

  private loadCatalogOptions(field: ClosingField): void {
    if (!field.ValidationRegex) {
      return;
    }

    const sub = this.catalogsService.getClosingFieldCatalog(field.ValidationRegex).subscribe((res) => {
      field.Catalog = res?.Data ?? [];
    });
    this.catalogSubscriptions.push(sub);
  }

  private clearCatalogSubscriptions(): void {
    for (const sub of this.catalogSubscriptions) {
      sub.unsubscribe();
    }
    this.catalogSubscriptions = [];
  }

  private createControl(field: ClosingField): FormControl {
    let initialValue: any = '';
    if (field.ComponentType === 'File') {
      initialValue = [];
    } else if (field.ComponentType === 'CatalogDropDownList') {
      initialValue = 0;
    }
    return new FormControl(initialValue);
  }

  private configureControl(control: FormControl, field: ClosingField): void {
    control.setValidators(this.buildValidators(field));
    control.updateValueAndValidity({ emitEvent: false });
  }

  private buildValidators(field: ClosingField): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (field.IsMandatory) {
      validators.push(Validators.required);
    }

    if (
      field.ComponentType !== 'File' &&
      field.ComponentType !== 'DropDownList' &&
      field.ComponentType !== 'CatalogDropDownList' &&
      field.ValidationRegex
    ) {
      validators.push(this.createRegexrValidator(field.ValidationRegex));
    }

    return validators;
  }

  private createRegexrValidator(regexr: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      !control.value || new RegExp(regexr).test(String(control.value))
        ? null
        : { errorGeneral: 'El formato es incorrecto' };
  }
}
